<template>
  <div class="page-search-result container">
    <base-bread-crumb :items="breadcrumb" />
    <div class="search-result__header">
      <div class="search-result__title-container d-flex align-items-center">
        <div class="search-result__title">All results for “{{ displaySearchText }}”</div>
        <div class="search-result__count">({{ totalItems }}) results</div>
      </div>

      <search-bar
        v-model="searchKeyWords"
        class="search-bar search-text-input"
        @search="onSearch"
        @reset="resetSearchText"
      >
        <template #button-search-outer="{ submitSearch }">
          <b-button class="search-button" variant="primary" @click="submitSearch"
            >Search</b-button
          ></template
        >
      </search-bar>
      <div class="popular-search-container">
        <div class="popular-search-title">Popular searches&nbsp;:</div>
        <div class="popular-search-term">
          <div
            v-for="(term, index) in popularSearchTerms"
            :key="index"
            class="popular-search-term-item"
            @click="onPopularSearch(term)"
          >
            {{ term }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="!isLoading" class="search-result__content">
      <!-- Recommended Section -->
      <div>
        <div v-if="stacks.length > 0">
          <!-- Using LearningUnitCardList for recommended items -->

          <div>
            <stack-card-list :items="stacks" :item-class="['stack-card']"
              ><template #header>
                <div class="card-title text-primary-dark">Skill set ({{ stacks.length }})</div>
              </template></stack-card-list
            >
          </div>
        </div>
        <div v-if="reccommendsLu.length > 0">
          <!-- Using LearningUnitCardList for recommended items -->

          <div>
            <learning-unit-card-list :items="reccommendsLu" :item-class="['learning-card']"
              ><template #header>
                <div class="card-title text-primary-dark">
                  Recommended skills for growth ({{ reccommendsLu.length }})
                </div>
              </template></learning-unit-card-list
            >
          </div>
        </div>

        <!-- Non-Recommended Section -->
        <div v-if="nonReccommendsLu.length > 0">
          <!-- Using LearningUnitCardList for non-recommended items -->

          <div>
            <learning-unit-card-list
              v-if="nonReccommendsLu.length > 0"
              :items="nonReccommendsLu"
              :item-class="['learning-card']"
            >
              <template #header>
                <div class="card-title">
                  All key skills for success ({{ nonReccommendsLu.length }})
                </div>
              </template>
            </learning-unit-card-list>
          </div>
        </div>
        <div v-else-if="totalItems === 0">
          <div class="no-results-container">
            <p class="text-black-50 mb-0">Sorry, we couldn't find any results</p>
            <p class="text-black-50 mb-3">Try adjusting your search, Here are some ideas:</p>
            <ul class="search-tips-list">
              <li class="search-tip-item text-black-50">
                Make sure all words are spelled correctly
              </li>
              <li class="search-tip-item text-black-50">Try different search terms</li>
              <li class="search-tip-item text-black-50">Try more general search terms</li>
            </ul>
          </div>
          <div v-if="recommendedStacks.length > 0">
            <!-- Using LearningUnitCardList for recommended items -->
            <hr />
            <div>
              <stack-card-list :items="recommendedStacks" :item-class="['stack-card']"
                ><template #header>
                  <div class="card-title text-primary-dark">Skill set packages</div>
                </template></stack-card-list
              >
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="text-center py-4">
      <b-spinner label="Loading..." variant="primary" />
    </div>
  </div>
</template>

<script>
import BaseBreadCrumb from '@/components/base-components/BaseBreadCrumb.vue';
import LearningUnitCardList from '@/views/LearningUnit/components/LearningUnitCardList.vue';
import StackCardList from '../Stack/components/StackCardList.vue';
import SearchBar from './components/SearchBar.vue';

import Utility from '@/models/Utility';
import Setting from '@/models/Setting';
import Stack from '@/models/Stack';
export default {
  components: {
    BaseBreadCrumb,
    LearningUnitCardList,
    StackCardList,
    SearchBar
  },
  data() {
    return {
      fetching: false,
      searchKeyWords: [],
      displaySearchText: '',

      stacks: [],
      reccommendsLu: [],
      nonReccommendsLu: [],
      recommendedStacks: [],
      totalItems: 0,
      isLoading: false,
      popularSearchTerms: []
    };
  },
  computed: {
    isSearchTextEmpty() {
      return this.searchKeyWords.trim() === '';
    },
    breadcrumb() {
      return [
        {
          key: 'explore',
          name: 'Explore',
          to: {
            name: 'learning-unit'
          }
        },
        {
          key: 'search',
          name: `search : ${this.displaySearchText}`,
          to: {}
        }
      ];
    }
  },
  created() {
    // Check if there's a search query in the URL
    if (this.$route.query.q) {
      const querySearch = this.$route.query.q;
      this.searchKeyWords = Array.isArray(querySearch) ? querySearch : [querySearch];
      this.displaySearchText = Array.isArray(querySearch) ? querySearch.join(' ') : querySearch;
    }
    this.fetchPopularSearchTerms();
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.isLoading = true;

      this.nonReccommendsLu = [];
      this.reccommendsLu = [];
      this.stacks = [];
      this.recommendedStacks = [];
      this.totalItems = 0;

      try {
        await this.search(this.searchKeyWords);
      } catch (error) {
        console.error('Error fetching data:', error);
        // If there's an error during search, ensure we reset the loading state
      } finally {
        this.isLoading = false;
      }
    },
    async fetchPopularSearchTerms() {
      // Simulate fetching popular search terms
      // In a real application, you would replace this with an API call
      const {
        response: { data }
      } = await Setting.api().fetchPopularSearches();
      this.popularSearchTerms = data;
    },
    async fetchRecommnedsStacks() {
      try {
        const {
          response: { data }
        } = await Stack.api().findStackExplore(3);
        this.recommendedStacks = data.items || [];
      } catch (error) {
        console.error('Error fetching recommended stacks:', error);
      }
    },
    onSearch(searchKeyWords) {
      this.searchKeyWords = searchKeyWords;
      // We'll let the search method handle updating the URL
      this.$emit('search', searchKeyWords);
      this.fetchData();
    },
    formatedDatetime(datetime, format) {
      return this.$moment(datetime).format(format);
    },

    async search(searchKeyWords) {
      // Convert array to string with spaces if it's an array
      if (Array.isArray(searchKeyWords)) {
        searchKeyWords = searchKeyWords.join(' ');
      }
      this.displaySearchText = searchKeyWords;

      // Update URL query parameter without reloading the page
      this.$router
        .push({
          query: { ...this.$route.query, q: searchKeyWords }
        })
        .catch((error) => {
          if (error.name !== 'NavigationDuplicated') {
            throw error;
          }
        });

      this.$emit('search', searchKeyWords);

      try {
        const {
          response: { data }
        } = await Utility.api().searchResult(searchKeyWords);
        if (data.total === 0) {
          // If no results, fetch recommended stacks
          await this.fetchRecommnedsStacks();
        } else {
          this.reccommendsLu = data.recommended_learning_units;
          this.nonReccommendsLu = data.non_recommended_learning_units;
          this.stacks = data.stacks;
          this.totalItems = data.total;
          // If there are results, reset recommended stacks
          this.recommendedStacks = [];
        }
      } catch (error) {
        // Handle 400 error or any other API error
        console.error('Search API error:', error);
        // Set results to empty arrays
        this.reccommendsLu = [];
        this.nonReccommendsLu = [];
        this.stacks = [];
        this.totalItems = 0;
      }
    },
    onPopularSearch(term) {
      // Update searchKeyWords array with the term
      this.searchKeyWords = [term];
      this.displaySearchText = term;

      // Update URL query parameter without reloading the page
      this.$router
        .push({
          query: { ...this.$route.query, q: term }
        })
        .catch((error) => {
          if (error.name !== 'NavigationDuplicated') {
            throw error;
          }
        });

      // Emit the search event with the term
      this.$emit('search', term);

      try {
        this.fetchData();
      } catch (error) {
        console.error('Error when searching for popular term:', error);
        // Set results to empty arrays in case of error
        this.reccommendsLu = [];
        this.nonReccommendsLu = [];
        this.stacks = [];
        this.totalItems = 0;
        this.isLoading = false;
      }
    },
    resetSearchText() {
      this.searchKeyWords = [];
      this.displaySearchText = '';

      // Remove query parameter from URL
      const query = { ...this.$route.query };
      delete query.q;
      this.$router.push({ query }).catch((error) => {
        if (error.name !== 'NavigationDuplicated') {
          throw error;
        }
      });

      this.$emit('search', '');
      this.fetchData();
    }
  }
};
</script>

<style lang="scss" scoped>
.page-search-result {
  padding: 0;
}

.search-result__title-container {
  margin-bottom: 16px;
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  align-items: center;
}
.search-result__header {
  padding: 0;
  align-items: center;

  .search-result__title {
    font-size: 32px;
    font-weight: 700;
  }

  ::v-deep .search-text-input input,
  .search-button {
    height: 55px;
  }

  .search-result__count {
    font-size: 20px;
    color: #7a7b97;
    margin-left: 8px;
  }
}

.popular-search-container {
  margin-top: 24px;
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 16px;

  .popular-search-title {
    min-width: max-content;
  }

  .popular-search-term {
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    gap: 16px;
  }
  .popular-search-term-item {
    padding: 4px 16px;
    border: 1px solid var(--primary);
    border-radius: 4px;
    color: var(--primary);
    cursor: pointer;
  }
}

.card-title {
  font-weight: 700;
  font-size: 20px;
  color: #3b3e66;
  margin-top: 40px;
  margin-bottom: 20px;
}

.button-clear-search {
  width: 40px;
  justify-content: center;
}

.no-results-container {
  padding: 28px 0;
}

@media screen and (max-width: 767px) {
  .search-result__header {
    .search-result__title {
      font-size: 20px;
    }
    .search-result__count {
      font-size: 16px;
    }
  }
  .popular-search-container {
    flex-direction: column;

    .popular-search-term-item {
      gap: 8px;
    }
  }
}

@media screen and (max-width: 575px) {
  .page-search-result {
    padding: 0 16px;
  }
}
</style>
